import {useState} from 'react'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {MoreHorizontal} from 'lucide-react'
import {useI18n} from '@/i18n'
import {api} from '@/api/client'
import {iamBase} from '@/lib/runtime-config'
import {useToast} from '@/hooks/use-toast'
import {Button} from '@/components/ui/button'
import {TargetSystemLink} from '@/components/shared/TargetSystemLink'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import type {Source} from '@/types/source'

/**
 * Entity type slug used in the authority release / transfer endpoint paths.
 * The same slugs the backend controllers register at
 * {@code /api/i/{entityType}/{id}/attributes/{attrName}/release-authority}.
 */
export type AuthorityEntityType =
    | 'identities'
    | 'parties'
    | 'resources'
    | 'function-groups'

/**
 * Subset of an attribute's wire shape needed to render the badge.
 *
 * Mirrors {@code valueAuthorityTargetSystemId} / {@code Name} on
 * {@code AttributeValue} (Spec §2.3 — multi-source-sync).
 */
export interface AttributeAuthorityFields {
    valueAuthorityTargetSystemId?: string | null
    valueAuthorityTargetSystemName?: string | null
}

/**
 * Information needed to dispatch Release / Transfer authority API calls.
 * Pass {@code undefined} on read-only surfaces (snapshots, history, my-access)
 * to suppress the action menu entirely.
 */
export interface AuthorityActionContext {
    entityType: AuthorityEntityType
    entityId: string
    attributeName: string
    /** Display label shown in dialog copy (`displayName ?? name`). */
    attributeDisplayName: string
    /**
     * Sources currently linked to this entity. The Transfer-Authority dialog
     * picker lists only systems with an active source row here (Spec §6.2).
     */
    sources: Source[]
    /** Cache keys to invalidate after a successful release/transfer. */
    invalidateQueryKeys?: ReadonlyArray<ReadonlyArray<unknown>>
}

export interface AttributeAuthorityBadgeProps {
    attribute: AttributeAuthorityFields
    /** Hide action menu (used in viewer-role view + non-editable contexts). */
    readOnly?: boolean
    /** Append "(historical)" label distinction (snapshots, history views). */
    historical?: boolean
    /** Required when {@code readOnly} is false to enable Release / Transfer actions. */
    actions?: AuthorityActionContext
    'data-testid'?: string
}

/* eslint-disable i18next/no-literal-string -- CSS class names, testid suffixes, API paths */

/**
 * Attribute authority indicator (Spec §6.2). Renders one of two pill states:
 *
 *   - <strong>Owned by {systemName}</strong> — when {@code valueAuthorityTargetSystemId} is set
 *   - <strong>Unclaimed</strong> — when null (manual edits welcome; next sync may claim)
 *
 * On narrow viewports (<640px) the label is hidden and only the icon is shown,
 * with a tooltip restoring the full text.
 *
 * For owned attributes a kebab menu offers Release / Transfer authority — hidden
 * when {@code readOnly}, {@code historical}, or no {@code actions} prop is passed.
 */
export function AttributeAuthorityBadge({
    attribute,
    readOnly = false,
    historical = false,
    actions,
    'data-testid': testId,
}: AttributeAuthorityBadgeProps) {
    const i18n = useI18n()
    const owned = attribute.valueAuthorityTargetSystemId != null
    const systemName = attribute.valueAuthorityTargetSystemName ?? ''
    const showActionMenu = owned && !readOnly && !historical && actions != null

    const linkSystemId = attribute.valueAuthorityTargetSystemId ?? null

    const label = owned
        ? linkSystemId != null
            ? <span
                data-testid={testId ?? 'attribute-authority-badge'}
                data-owned="true"
                data-historical={historical ? 'true' : undefined}
              >
                <TargetSystemLink
                    targetSystemId={linkSystemId}
                    targetSystemName={systemName + (historical ? ` ${i18n.authority.historical}` : '')}
                />
              </span>
            : <span
                className="text-xs text-muted-foreground"
                data-testid={testId ?? 'attribute-authority-badge'}
                data-owned="true"
                data-historical={historical ? 'true' : undefined}
              >
                {systemName}
              </span>
        : <span
            className="text-xs text-muted-foreground"
            data-testid={testId ?? 'attribute-authority-badge'}
            data-owned="false"
          >
            {i18n.authority.unclaimed}
          </span>

    return (
        <span className="inline-flex items-center gap-1 justify-end w-full">
            {label}
            {showActionMenu && actions != null && (
                <AuthorityActionsMenu
                    attribute={attribute}
                    actions={actions}
                    testId={testId ?? 'attribute-authority-badge'}
                />
            )}
        </span>
    )
}

interface AuthorityActionsMenuProps {
    attribute: AttributeAuthorityFields
    actions: AuthorityActionContext
    testId: string
}

function AuthorityActionsMenu({attribute, actions, testId}: AuthorityActionsMenuProps) {
    const i18n = useI18n()
    const [releaseOpen, setReleaseOpen] = useState(false)
    const [transferOpen, setTransferOpen] = useState(false)

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        aria-label={i18n.authority.actionsMenu}
                        data-testid={`${testId}-menu`}
                    >
                        <MoreHorizontal className="h-3.5 w-3.5" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <DropdownMenuItem
                        data-testid={`${testId}-release`}
                        onSelect={(e) => {
                            e.preventDefault()
                            setReleaseOpen(true)
                        }}
                    >
                        {i18n.authority.release}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        data-testid={`${testId}-transfer`}
                        onSelect={(e) => {
                            e.preventDefault()
                            setTransferOpen(true)
                        }}
                    >
                        {i18n.authority.transfer}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            {releaseOpen && (
                <ReleaseAuthorityDialog
                    open={releaseOpen}
                    onOpenChange={setReleaseOpen}
                    attribute={attribute}
                    actions={actions}
                />
            )}
            {transferOpen && (
                <TransferAuthorityDialog
                    open={transferOpen}
                    onOpenChange={setTransferOpen}
                    actions={actions}
                />
            )}
        </>
    )
}

interface ReleaseAuthorityDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    attribute: AttributeAuthorityFields
    actions: AuthorityActionContext
}

function ReleaseAuthorityDialog({
    open,
    onOpenChange,
    attribute,
    actions,
}: ReleaseAuthorityDialogProps) {
    const i18n = useI18n()
    const {toast} = useToast()
    const queryClient = useQueryClient()
    const systemName = attribute.valueAuthorityTargetSystemName ?? ''

    const mutation = useMutation({
        mutationFn: () =>
            api.post<unknown>(
                iamBase(
                    `/${actions.entityType}/${actions.entityId}/attributes/${encodeURIComponent(actions.attributeName)}/release-authority`,
                ),
                {},
            ),
        onSuccess: () => {
            toast({description: i18n.authority.releaseSuccess})
            invalidateAuthorityCaches(queryClient, actions)
            onOpenChange(false)
        },
    })

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="release-authority-dialog">
                <DialogHeader>
                    <DialogTitle>
                        {i18n.authority.releaseTitle({attributeName: actions.attributeDisplayName})}
                    </DialogTitle>
                    <DialogDescription>
                        {i18n.authority.releaseBody({
                            attributeName: actions.attributeDisplayName,
                            systemName,
                        })}
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={mutation.isPending}
                        data-testid="release-authority-cancel"
                    >
                        {i18n.common.cancel}
                    </Button>
                    <Button
                        variant="warning"
                        onClick={() => mutation.mutate()}
                        disabled={mutation.isPending}
                        data-testid="release-authority-confirm"
                    >
                        {i18n.authority.releaseConfirm}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

interface TransferAuthorityDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    actions: AuthorityActionContext
}

function TransferAuthorityDialog({open, onOpenChange, actions}: TransferAuthorityDialogProps) {
    const i18n = useI18n()
    const {toast} = useToast()
    const queryClient = useQueryClient()
    const eligibleSources = actions.sources

    const initialTarget =
        eligibleSources.length === 1 ? eligibleSources[0]!.targetSystemId : ''
    const [targetSystemId, setTargetSystemId] = useState<string>(initialTarget)

    const mutation = useMutation({
        mutationFn: () =>
            api.post<unknown>(
                iamBase(
                    `/${actions.entityType}/${actions.entityId}/attributes/${encodeURIComponent(actions.attributeName)}/transfer-authority`,
                ),
                {targetSystemId},
            ),
        onSuccess: () => {
            const targetName =
                eligibleSources.find((s) => s.targetSystemId === targetSystemId)?.targetSystemName ??
                ''
            toast({description: i18n.authority.transferSuccess({systemName: targetName})})
            invalidateAuthorityCaches(queryClient, actions)
            onOpenChange(false)
        },
    })

    const noEligible = eligibleSources.length === 0

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="transfer-authority-dialog">
                <DialogHeader>
                    <DialogTitle>
                        {i18n.authority.transferTitle({attributeName: actions.attributeDisplayName})}
                    </DialogTitle>
                    <DialogDescription>
                        {i18n.authority.transferBody({attributeName: actions.attributeDisplayName})}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 py-2">
                    {noEligible ? (
                        <p className="text-sm text-muted-foreground" data-testid="transfer-authority-no-sources">
                            {i18n.authority.transferNoEligibleSources}
                        </p>
                    ) : (
                        <>
                            <label className="text-sm font-medium" htmlFor="transfer-authority-target">
                                {i18n.authority.transferTargetLabel}
                            </label>
                            <Select value={targetSystemId} onValueChange={setTargetSystemId}>
                                <SelectTrigger
                                    id="transfer-authority-target"
                                    data-testid="transfer-authority-target-trigger"
                                >
                                    <SelectValue placeholder={i18n.authority.transferTargetPlaceholder} />
                                </SelectTrigger>
                                <SelectContent>
                                    {eligibleSources.map((src) => (
                                        <SelectItem
                                            key={src.targetSystemId}
                                            value={src.targetSystemId}
                                            data-testid={`transfer-authority-target-${src.targetSystemId}`}
                                        >
                                            {src.targetSystemName}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </>
                    )}
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={mutation.isPending}
                        data-testid="transfer-authority-cancel"
                    >
                        {i18n.common.cancel}
                    </Button>
                    <Button
                        onClick={() => mutation.mutate()}
                        disabled={mutation.isPending || noEligible || !targetSystemId}
                        data-testid="transfer-authority-confirm"
                    >
                        {i18n.authority.transferConfirm}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

function invalidateAuthorityCaches(
    queryClient: ReturnType<typeof useQueryClient>,
    actions: AuthorityActionContext,
): void {
    queryClient.invalidateQueries({queryKey: [actions.entityType]})
    queryClient.invalidateQueries({queryKey: [actions.entityType, actions.entityId, 'attributes']})
    for (const key of actions.invalidateQueryKeys ?? []) {
        queryClient.invalidateQueries({queryKey: [...key]})
    }
}

/* eslint-enable i18next/no-literal-string */
